import type { ChannelBroadcastType } from '@pulsify/shared'
import { Calendar, Gift, Link2, Megaphone, Users, X } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import {
  useChannelAudience,
  useChannelBroadcasts,
  useSendChannelBroadcast,
} from '~/entities/channel/model/use-channel'
import { useAdminDrops, useDropLinkSearch } from '~/entities/drop/model/use-drops'
import { ChannelInbox } from '~/features/admin/channel-inbox'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Textarea } from '~/shared/ui/textarea'

const broadcastTypes = ['announcement', 'update', 'event', 'drop'] as const

const broadcastTypeLabel: Record<ChannelBroadcastType, () => string> = {
  announcement: m.channelTypeAnnouncement,
  update: m.channelTypeUpdate,
  event: m.channelTypeEvent,
  drop: m.channelTypeDrop,
}

export function ChannelPage() {
  const [message, setMessage] = useState('')
  const [broadcastType, setBroadcastType] = useState<ChannelBroadcastType>('announcement')
  const [city, setCity] = useState('')
  const [debouncedCity, setDebouncedCity] = useState('')
  const [eventSearch, setEventSearch] = useState('')
  const [debouncedEventSearch, setDebouncedEventSearch] = useState('')
  const [attachKind, setAttachKind] = useState<'none' | 'event' | 'drop'>('none')
  const [linkedEvent, setLinkedEvent] = useState<{ id: string; name: string } | null>(null)
  const [linkedDrop, setLinkedDrop] = useState<{ id: string; name: string } | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const messageFieldId = useId()
  const cityFieldId = useId()

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCity(city), 300)
    return () => clearTimeout(timer)
  }, [city])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEventSearch(eventSearch), 300)
    return () => clearTimeout(timer)
  }, [eventSearch])

  const audience = useChannelAudience(debouncedCity)
  const broadcasts = useChannelBroadcasts()
  const send = useSendChannelBroadcast()
  const eventResults = useDropLinkSearch(
    attachKind === 'event' && !linkedEvent ? 'event' : 'none',
    debouncedEventSearch,
  )
  const adminDrops = useAdminDrops()
  const attachableDrops = (adminDrops.data ?? []).filter(
    (d) => d.status === 'live' || d.status === 'drawn',
  )

  const audienceCount = audience.data ?? 0
  const canSend = message.trim().length > 0 && !send.isPending && audienceCount > 0

  const handleSend = () => {
    setConfirmOpen(false)
    send.mutate(
      {
        message: message.trim(),
        type: broadcastType,
        eventId: attachKind === 'event' ? linkedEvent?.id : undefined,
        dropId: attachKind === 'drop' ? linkedDrop?.id : undefined,
        city: debouncedCity.trim() || undefined,
      },
      {
        onSuccess: () => {
          setMessage('')
          setBroadcastType('announcement')
          setAttachKind('none')
          setLinkedEvent(null)
          setLinkedDrop(null)
          setEventSearch('')
        },
      },
    )
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(getLocale(), { dateStyle: 'medium', timeStyle: 'short' })

  return (
    <div className="p-4 py-8 sm:p-8 sm:py-24">
      <div className="mx-auto max-w-5xl space-y-8">
        <div>
          <h1 className="mb-2 font-bold text-slate-900 dark:text-slate-100 text-3xl">
            {m.channelTitle()}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">{m.channelDescription()}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{m.channelComposeTitle()}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={messageFieldId}>{m.channelMessageLabel()}</Label>
              <Textarea
                id={messageFieldId}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={m.channelMessagePlaceholder()}
                rows={4}
                maxLength={2000}
              />
            </div>

            <div className="gap-4 grid sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{m.channelTypeLabel()}</Label>
                <Select
                  value={broadcastType}
                  onValueChange={(v) => setBroadcastType(v as ChannelBroadcastType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {broadcastTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {broadcastTypeLabel[type]()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{m.channelAttachLabel()}</Label>
                <div className="flex gap-2">
                  <Select
                    value={attachKind}
                    onValueChange={(v) => {
                      setAttachKind(v as 'none' | 'event' | 'drop')
                      setLinkedEvent(null)
                      setLinkedDrop(null)
                      setEventSearch('')
                    }}
                  >
                    <SelectTrigger className="w-32 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{m.channelAttachNone()}</SelectItem>
                      <SelectItem value="event">{m.channelAttachEvent()}</SelectItem>
                      <SelectItem value="drop">{m.channelAttachDrop()}</SelectItem>
                    </SelectContent>
                  </Select>
                  {attachKind === 'event' && linkedEvent && (
                    <div className="flex flex-1 items-center gap-2 px-3 border border-slate-200 dark:border-slate-800 rounded-md min-w-0">
                      <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="flex-1 text-slate-900 dark:text-slate-100 text-sm truncate">
                        {linkedEvent.name}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-1"
                        title={m.channelClearEvent()}
                        onClick={() => setLinkedEvent(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  {attachKind === 'event' && !linkedEvent && (
                    <div className="relative flex-1">
                      <Input
                        value={eventSearch}
                        placeholder={m.channelEventSearchPlaceholder()}
                        onChange={(e) => setEventSearch(e.target.value)}
                      />
                      {(eventResults.data ?? []).length > 0 && eventSearch.trim().length >= 2 && (
                        <div className="top-full right-0 left-0 z-10 absolute bg-white dark:bg-slate-950 mt-1 border border-slate-200 dark:border-slate-800 rounded-md max-h-56 overflow-y-auto">
                          {(eventResults.data ?? []).map((result) => (
                            <button
                              key={result.id}
                              type="button"
                              className="flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-900 px-3 py-2 w-full text-left text-sm"
                              onClick={() => {
                                setLinkedEvent(result)
                                setEventSearch('')
                              }}
                            >
                              <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                              <span className="truncate">{result.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {attachKind === 'drop' && (
                    <Select
                      value={linkedDrop?.id ?? ''}
                      onValueChange={(id) => {
                        const drop = attachableDrops.find((d) => d.id === id)
                        setLinkedDrop(drop ? { id: drop.id, name: drop.title } : null)
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={m.channelDropPlaceholder()} />
                      </SelectTrigger>
                      <SelectContent>
                        {attachableDrops.map((drop) => (
                          <SelectItem key={drop.id} value={drop.id}>
                            <span className="flex items-center gap-2">
                              <Gift className="w-4 h-4 text-slate-400" />
                              {drop.title}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={cityFieldId}>{m.channelCityLabel()}</Label>
                <Input
                  id={cityFieldId}
                  value={city}
                  placeholder={m.channelCityPlaceholder()}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 text-sm">
                <Users className="w-4 h-4" />
                {m.channelAudience({ count: audienceCount })}
              </div>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!canSend}
                onClick={() => setConfirmOpen(true)}
              >
                <Megaphone className="mr-2 w-4 h-4" />
                {m.channelSend()}
              </Button>
            </div>
          </CardContent>
        </Card>

        <ChannelInbox />

        <Card>
          <CardHeader>
            <CardTitle>{m.channelHistoryTitle()}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {(broadcasts.data ?? []).length === 0 ? (
              <p className="px-6 pb-6 text-slate-500 dark:text-slate-400 text-sm">
                {m.channelHistoryEmpty()}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-slate-200 dark:border-slate-800 border-b text-left text-slate-500 dark:text-slate-400">
                    <th className="px-6 py-3 font-medium">{m.channelColDate()}</th>
                    <th className="px-6 py-3 font-medium">{m.channelColType()}</th>
                    <th className="px-6 py-3 font-medium">{m.channelColMessage()}</th>
                    <th className="px-6 py-3 font-medium">{m.channelColEvent()}</th>
                    <th className="px-6 py-3 font-medium">{m.channelColAudience()}</th>
                    <th className="px-6 py-3 font-medium">{m.channelColStatus()}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {(broadcasts.data ?? []).map((b) => (
                    <tr key={b.id}>
                      <td className="px-6 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {formatDate(b.createdAt)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <Badge variant="secondary">{broadcastTypeLabel[b.type]()}</Badge>
                      </td>
                      <td className="px-6 py-3 max-w-md">
                        <span className="block truncate">{b.message}</span>
                      </td>
                      <td className="px-6 py-3 text-slate-500 dark:text-slate-400">
                        {b.eventTitle ?? b.dropTitle ?? '-'}
                      </td>
                      <td className="px-6 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {b.city ?? m.channelAllUsers()} · {b.sentCount}/{b.recipientCount}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {b.completedAt == null ? (
                          <span className="text-blue-600 dark:text-blue-400">
                            {m.channelStatusSending()}
                          </span>
                        ) : (
                          <span className="text-slate-500 dark:text-slate-400">
                            {m.channelStatusDone()}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.channelConfirmTitle()}</AlertDialogTitle>
            <AlertDialogDescription>
              {m.channelConfirmBody({ count: audienceCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSend}
            >
              {m.channelConfirmSend()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
